import { FormEvent, useEffect, useState } from 'react'
import { partnerApi } from '@/services/api/partner'
import { useAuthStore } from '@/store/authStore'
import type { School, SuperadminInfo } from '@/services/api/ultraadmin'
import { extractErrorMessage } from '@/utils/errorHandler'

export function PartnerSuperadminsPage() {
  const partnerLevel = useAuthStore((s) => s.user?.partner_level)
  const [schools, setSchools] = useState<School[]>([])
  const [schoolId, setSchoolId] = useState('')
  const [rows, setRows] = useState<SuperadminInfo[]>([])
  const [error, setError] = useState<string | null>(null)
  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [transferId, setTransferId] = useState('')
  const [targetUserId, setTargetUserId] = useState('')
  const [transferCandidates, setTransferCandidates] = useState<{ id: string; email: string; first_name: string | null; last_name: string | null }[]>([])

  const loadSchools = async () => {
    const list = await partnerApi.listSchools()
    setSchools(list)
    if (!schoolId && list[0]) setSchoolId(list[0].id)
  }
  const loadSa = async (id: string) => {
    setRows(await partnerApi.listSuperadmins(id))
  }

  useEffect(() => { loadSchools().catch((e) => setError(extractErrorMessage(e, { loadError: 'Fehler' }))) }, [])
  useEffect(() => { if (schoolId) loadSa(schoolId).catch((e) => setError(extractErrorMessage(e, { loadError: 'Fehler' }))) }, [schoolId])

  const onCreate = async (e: FormEvent) => {
    e.preventDefault()
    try {
      await partnerApi.createSuperadmin({ email, first_name: firstName, last_name: lastName, school_id: schoolId })
      setEmail(''); setFirstName(''); setLastName('')
      await loadSa(schoolId)
    } catch (err: unknown) {
      setError(extractErrorMessage(err, { loadError: 'Anlegen fehlgeschlagen' }))
    }
  }

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold">Superadmins</h1>
      {error && <div className="p-3 bg-red-100 text-red-700 rounded">{error}</div>}
      <select className="border p-2" value={schoolId} onChange={(e) => setSchoolId(e.target.value)}>
        {schools.map((s) => <option key={s.id} value={s.id}>{s.name}{s.is_active ? '' : ' (deaktiviert)'}</option>)}
      </select>
      <table className="min-w-full bg-white rounded-lg shadow">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-2 text-left text-xs uppercase">Name</th>
            <th className="px-4 py-2 text-left text-xs uppercase">E-Mail</th>
            <th className="px-4 py-2 text-left text-xs uppercase">Status</th>
            <th className="px-4 py-2 text-left text-xs uppercase">Aktionen</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t">
              <td className="px-4 py-2">{r.last_name}, {r.first_name}</td>
              <td className="px-4 py-2">{r.email}</td>
              <td className="px-4 py-2">{r.is_active ? 'aktiv' : 'inaktiv'}</td>
              <td className="px-4 py-2 space-x-2 text-sm">
                {r.is_active
                  ? <button type="button" onClick={() => partnerApi.deactivateSuperadmin(r.id).then(() => loadSa(schoolId)).catch((e) => setError(extractErrorMessage(e, { loadError: 'Fehler' })))}>Deaktivieren</button>
                  : <button type="button" onClick={() => partnerApi.activateSuperadmin(r.id).then(() => loadSa(schoolId)).catch((e) => setError(extractErrorMessage(e, { loadError: 'Fehler' })))}>Aktivieren</button>}
                <button type="button" onClick={() => partnerApi.resendInvite(r.id).catch((e) => setError(extractErrorMessage(e, { loadError: 'Fehler' })))}>Invite erneut</button>
                {partnerLevel === 'owner' && <button type="button" onClick={() => {
                  setTransferId(r.id)
                  setTargetUserId('')
                  partnerApi.listSchoolUsers(schoolId).then((res) => setTransferCandidates(res.users)).catch((e) => setError(extractErrorMessage(e, { loadError: 'Fehler' })))
                }}>Transfer</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <form onSubmit={onCreate} className="bg-white p-4 rounded-lg shadow space-y-2 max-w-md">
        <h2 className="font-semibold">Superadmin anlegen</h2>
        <input className="border w-full p-2" placeholder="Vorname" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
        <input className="border w-full p-2" placeholder="Nachname" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
        <input className="border w-full p-2" placeholder="E-Mail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        <button type="submit" className="bg-blue-600 text-white px-3 py-1 rounded">Anlegen</button>
        {partnerLevel !== 'owner' && <p className="text-xs text-gray-500">Transfer nur für Inhaber.</p>}
      </form>
      {transferId && partnerLevel === 'owner' && (
        <form className="bg-white p-4 rounded-lg shadow max-w-md space-y-2" onSubmit={async (e) => {
          e.preventDefault()
          try {
            await partnerApi.transferSuperadmin(transferId, targetUserId)
            setTransferId('')
            await loadSa(schoolId)
          } catch (err: unknown) {
            setError(extractErrorMessage(err, { loadError: 'Transfer fehlgeschlagen' }))
          }
        }}>
          <h2 className="font-semibold">Transfer</h2>
          <select className="border w-full p-2" value={targetUserId} onChange={(e) => setTargetUserId(e.target.value)} required>
            <option value="">Zielperson wählen</option>
            {transferCandidates.map((u) => (
              <option key={u.id} value={u.id}>{u.last_name}, {u.first_name} ({u.email})</option>
            ))}
          </select>
          <div className="flex gap-2">
            <button type="button" onClick={() => setTransferId('')}>Abbrechen</button>
            <button type="submit" className="bg-blue-600 text-white px-3 py-1 rounded">Übertragen</button>
          </div>
        </form>
      )}
    </div>
  )
}
